<template>
  <div class="ds-control ds-table-layout" :class="getCss">
    <div class="ds-table-layout-box" :style="tableBorderStyle">
      <div ref="observer"></div>
      <table
        ref="table"
        class="ds-table-layout-table"
        v-resize.x.y="{
          colList: 'columnsInfo',
          rowList: 'rows',
          onChangeCol: onChangeCol,
          onChangeRow: onChangeRow
        }"
      >
        <thead>
          <tr>
            <th style="padding: 0" v-for="(col, $index) in columnsInfo" :key="$index" ref="th"></th>
          </tr>
        </thead>
        <tbody>
          <slot name="prepend"></slot>
          <slot name="default"></slot>
          <slot name="append"></slot>
        </tbody>
      </table>
      <div
        v-if="isDesign"
        v-show="rangeWrap.show"
        :style="selectedRangeStyle"
        class="selected-range"
      ></div>
    </div>
  </div>
</template>
<script>
let directives = null;
let tableFun = null;
const minCellWidth = 50;

function doFun(vm, funName, ...args) {
  if (tableFun) {
    return tableFun[funName].call(vm, ...args);
  }
  import(/*webpackChunkName:"platform_pc_layout_tableFun"*/ './tableFun').then(res => {
    tableFun = res;
    return tableFun[funName].call(vm, ...args);
  });
}

/**
 * @class DsfTable
 * @augments Control
 * @description 表格布局
 * @example
 * <DsfTable></DsfTable>
 */
export default dsf.component({
  name: "DsfTable",
  ctrlCaption: "表格布局",
  mixins: [$mixins.control],
  provide() {
    return {
      $tableLayout: this
    };
  },
  directives: {
    resize: {
      inserted(el, options, vnode) {
        if (vnode.context.isDesign) {
          doFun(this, 'directiveInserted', el, options, vnode);
        }
      },
      update(el, options, vnode) {
        if (vnode.context.isDesign) {
          doFun(this, 'directiveUpdate', el, options, vnode);
        }
      },
      unbind(el, options, vnode) {
        if (vnode.context.isDesign) {
          doFun(this, 'directiveUnbind', el, options, vnode);
        }
      },
    }
  },
  props: {
    // 复制粘贴进来的html片段
    clipboardString: {
      type: String,
      default: ""
    },
    slots: {
      type: Array,
      default() {
        return [
          {
            name: "default",
            controls: []
          }
        ];
      }
    },
    // 列宽设置
    columnsInfo: {
      type: Array,
      default() {
        return [];
      }
    },
    rowsInfo: {
      type: Array,
      default() {
        return [];
      }
    },
    showBorder: {
      type: Boolean,
      default: true
    },
    noInsideBorder: {
      type: Boolean,
      default: false
    },
    fixedLayout: {
      type: Boolean,
      default: true
    },
    isBlockControl: {
      type: Boolean,
      default: true
    },
    borderWidth: {
      type: Number,
      default: 1
    },
    borderStyle: {
      type: String,
      default: "solid"
    },
    borderColor: {
      type: String,
      default: ""
    },
    outBorderShowSite: {
      // 外边框显示的位置
      type: Array,
      default: () => {
        return ["top", "right", "bottom", "left"];
      }
    }
  },
  data() {
    this.__resizeObserver = null;
    this.__realColumnsInfo = [];
    return {
      rangeWrap: {
        show: false,
        startRowIndex: 0,
        endRowIndex: 0,
        startColIndex: 0,
        endColIndex: 0,
        width: 0,
        height: 0,
        top: 0,
        left: 0,
        selected: []
      },
      allCells: {},
      matrix: [],
      // isDown: false,
      downCell: null,
      cellNums: 0,
      tableRows: [],
      cellZIndex: 1,
      designInitRowCount: 4,
      designInitColumnCount: 4
    };
  },
  computed: {
    rows() {
      return this.getRows();
    },
    getCss() {
      return [
        ...this.$getClass(),
        // this.showBorder ? "" : "ds-no-border",
        this.borderStyle == "none" ? "ds-no-border" : "",
        // !this.visible ? 'ds-hide' : '',
        !this.noPadding ? "" : "ds-no-padding",
        this.fixedLayout ? "ds-table-fixed" : "",
        ...this.classList
      ];
    },
    outBorderWidth() {
      if (this.borderStyle == "double") {
        return this.unitSize(this.borderWidth);
      } else {
        return 0;
      }
    },
    tableBorderStyle() {
      // 外边框样式集合
      let style = {};
      if (this.borderStyle == "double") {
        let padding = this.noInsideBorder ? (this.isDesign ? "1px" : "0px") : this.outBorderWidth;
        if (this.outBorderShowSite.length === 4) {
          style["border"] = `${this.outBorderWidth} solid ${this.borderColor}`;
          style["padding"] = padding;
        } else {
          _.forEach(this.outBorderShowSite, (site) => {
            style[`border-${site}`] = `${this.outBorderWidth} solid ${this.borderColor}`;
            style[`padding-${site}`] = padding;
          });
        }
      }
      return style;
    },
    selectedRangeStyle() {
      // 设计器选中时的框样式
      let rangeWrap = this.rangeWrap;
      return {
        height: rangeWrap.height + "px",
        width: rangeWrap.width + "px",
        top: rangeWrap.top + "px",
        left: rangeWrap.left + "px"
      };
    }
  },
  watch: {
    columnsInfo: {
      handler() {
        this.updateRealColumnsInfo();
      },
      deep: true
    },
    borderWidth() {
      this.updateRealColumnsInfo();
    }
  },
  created() {
    if (this.isDesign && this.slots[0].controls.length === 0) {
      // 如果是粘贴进来的table
      if (this.clipboardString) {
        fromClipboardString.call(this);
      } else {
        // 设计模式下 默认情况
        let colCount = this.designInitColumnCount;
        for (let i = 0; i < colCount; i++) {
          addColumn.call(this);
        }
        let rowCount = this.designInitRowCount;
        //设计模式下默认生成4行
        for (let i = 0; i < rowCount; i++) {
          let row = addRow.call(this);
          for (let n = 0; n < colCount; n++) {
            addCell.call(this, row);
          }
        }
      }
    }
  },
  mounted() {
    if (this.isDesign) {
      doFun(this, 'parseMatrix');
    }
    this.__resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) {
        let {
          contentRect: { width: elWidth }
        } = entry;
        this.updateRealColumnsInfo(_.round(elWidth));
      }
    });
    this.__resizeObserver.observe(this.$refs.observer);
  },
  updated() {
    if (this.isDesign) {
      doFun(this, 'updateRangeWrap', this.rangeWrap.selected);
    }
  },
  beforeDestroy() {
    if (this.__resizeObserver) {
      this.__resizeObserver.disconnect();
    }
  },
  methods: {
    updateRealColumnsInfo(elWidth) {
      this.$nextTick(() => {
        if (elWidth === void 0) {
          if (!this.$refs.table) return;
          elWidth = this.$refs.observer.offsetWidth;
        }
        elWidth -= this.borderWidth;
        let allWidth = 0,
          scaleMap = {},
          autoMap = {},
          concreteMap = {},
          minWidth = minCellWidth,
          averageWidth = elWidth / this.columnsInfo.length;
        if (averageWidth < minWidth) {
          minWidth = averageWidth;
        }
        _.eachRight(this.columnsInfo, ({ width }, i) => {
          let w = parseFloat(width);
          if (!width || isNaN(w)) {
            autoMap[i] = "auto";
          } else if (/%$/.test(width)) {
            scaleMap[i] = w;
            allWidth += (w * elWidth) / 100;
          } else {
            concreteMap[i] = w;
            allWidth += w;
          }
        });
        let autoLength = _.values(autoMap).length;
        if (elWidth - allWidth < autoLength * minWidth) {
          let equivalentWidth = elWidth - autoLength * minWidth;
          _.each(concreteMap, (w, i) => {
            concreteMap[i] = (equivalentWidth * w) / allWidth;
          });
          _.each(scaleMap, (w, i) => {
            let OldWidth = (w * elWidth) / 100;
            concreteMap[i] = (equivalentWidth * OldWidth) / allWidth;
          });
          _.each(autoMap, (w, i) => {
            concreteMap[i] = minWidth;
          });
        } else {
          _.each(scaleMap, (w, i) => {
            concreteMap[i] = (w * elWidth) / 100;
          });
          let equivalentWidth = _.reduce(concreteMap, (s, n) => s + n, 0);
          let autoWidth = (elWidth - equivalentWidth) / autoLength;
          _.each(autoMap, (w, i) => {
            concreteMap[i] = autoWidth;
          });
        }
        let equivalentWidth = 0;
        _.each(concreteMap, (w, i) => {
          equivalentWidth += concreteMap[i] = _.round(w);
        });
        let offWidth = elWidth - equivalentWidth,
          u = offWidth > 0 ? 1 : -1;
        if (offWidth != 0) {
          let l = this.columnsInfo.length,
            i = l;
          while (offWidth) {
            offWidth -= u;
            concreteMap[--i] += u;
            if (i == 0) i = l;
          }
        }
        _.each(concreteMap, (w, i) => {
          if (this.__realColumnsInfo[i] !== w) {
            this.$refs.th[i].style.width = w + "px";
            this.__realColumnsInfo[i] = w;
          }
        });
        if (this.isDesign) {
          doFun(this, 'updateRangeWrap', this.rangeWrap.selected);
        }
      });
    },
    updateRangeWrap() {
      doFun(this, 'updateRangeWrap', this.rangeWrap.selected);
    },
    setSelectedCellsByPosition() {
      return doFun(this, 'setSelectedCellsByPosition');
    },
    getRows() {
      if (this.isDesign) {
        _.each(this.slots[0].controls, (row, i) => {
          row.rowIndex = i;
        });
        return this.slots[0].controls;
      } else {
        return this.tableRows;
      }
    },
    getCells(row) {
      return row.slots[0].controls;
    },
    getCellWidth(cell, index) {
      let width;
      if (cell) {
        if (cell.colspan === 1) {
          width = this.columnsInfo[index].width;
        }
      } else {
        width = this.columnsInfo[index].width;
      }
      return width || "auto";
    },
    isSelected(rindex, cindex) {
      let result = _.find(this.rangeWrap.selected, (c) => {
        return rindex === c.rowIndex && cindex === c.cellIndex;
      });
      return !!result;
    },
    // 列宽被改变
    onChangeCol(colList) {
      this.$emit("update:columnsInfo", colList);
      if (this.isDesign) {
        this.$nextTick(() => {
          doFun(this, 'updateRangeWrap', this.rangeWrap.selected);
        });
      }
    },
    // 行高被改变
    onChangeRow(index, height) {
      this.rows[index].height = height;
      if (this.isDesign) {
        this.$nextTick(() => {
          doFun(this, 'updateRangeWrap', this.rangeWrap.selected);
        });
      }
    },
    $childComponentAddToCell(cell, ctrl, slot) {},
    // 设置单元格样式
    setCellStyle(params) {
      doFun(this, 'setCellStyle', params);
    },
    setCellBorder(params) {
      doFun(this, 'setCellBorder', params);
    },
    setCellCustomColor(value, cb) {
      doFun(this, 'setCellCustomColor', value, cb);
    }
  },
  design: {
    isMask: false,
    //右键菜单处理
    contextMenuHandler(evt) {
      return doFun(this, 'contextMenuHandler', evt);
    },
    //设计器全局的mousedown事件，设计器会向下级所有组件触发,可用于清理当前触发过mousedown事件留下的痕迹
    clearMouseDownHandler() {
      doFun(this, 'clearMouseDownHandler');
    },
    //鼠标down事件处理
    mouseDownHandler(evt, designer, target) {
      doFun(this, 'mouseDownHandler', evt, designer, target);
    },
    //鼠标移动事件处理
    mouseMoveHandler(evt) {
      doFun(this, 'mouseMoveHandler', evt);
    },
    //鼠标up事件处理
    mouseUpHandler() {
      doFun(this, 'mouseUpHandler');
    }
  }
});

function fromClipboardString() {
  let clipboardTable = $(this.clipboardString).appendTo("body");
  const trs = clipboardTable.find("tr");
  let columns = [];
  for (let i = 0; i < trs.length; i++) {
    let tr = $(trs[i]);
    const tds = tr.find("td");
    let columnNum = 0;
    _.each(tds, (td) => {
      td = $(td);
      const colspan = ~~td.attr("colspan") || 1;
      columnNum += colspan;
      if (colspan <= 1);
      {
        columns[columnNum - 1] = {
          width: td.innerWidth()
        };
      }
    });
    let row = addRow.call(this);
    for (let j = 0; j < tds.length; j++) {
      const td = $(tds[j]);
      if (td) {
        const rowspan = ~~td.attr("rowspan") || 1;
        const colspan = ~~td.attr("colspan") || 1;
        const text = td.html();
        addCell.call(this, row, { rowspan, colspan, text });
      }
    }
  }
  let sum = 0;
  _.each(columns, (it) => {
    sum += ~~it.width;
  });
  for (let c = 0; c < columns.length; c++) {
    let w = (~~columns[c].width / sum) * 100;
    addColumn.call(this, null, w + "%");
  }
  clipboardTable.remove();
}

function addRow() {
  let row = createRow();
  this.slots[0].controls.push(row);
  return row;
}
function addCell(row, slots) {
  let cell = createCell(slots);
  row.slots[0].controls.push(cell);
  return cell;
}
function addColumn(index, width = "auto") {
  if (dsf.isUnDef(index) || index < 0) {
    this.columnsInfo.push({ width: width });
  } else {
    this.columnsInfo.splice(index, 0, { width: width });
  }
}


//创建列
function createCell(slots = {}) {
  return dsf.designer.createComponent(window.$components.platform.DsfTableCell, slots);
}
//创建行
function createRow() {
  return dsf.designer.createComponent(window.$components.platform.DsfTableRow);
}

</script>
